import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroServices } from "@/components/aero/AeroServices";
import { BtsShowcase } from "@/components/aero/BtsShowcase";
import { DirectorsDesk } from "@/components/aero/DirectorsDesk";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { ConsultationSection } from "@/components/aero/ConsultationSection";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG = "/assets/services-hero.jpg";

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white text-[#111827] overflow-x-clip"
      data-testid="services-page"
    >
      <AeroNav />

      {/* Page hero */}
      <section className="relative min-h-[62svh] flex items-end overflow-hidden bg-[#F8F6F2]" data-testid="services-hero">
        <img src={HERO_IMG} alt="Drone production unit on set" className="absolute inset-0 w-full h-full object-cover object-[center_20%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/20" />
        <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 w-full pb-14 pt-40">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease }} className="max-w-2xl">
            <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A] flex items-center gap-4">
              <span className="w-10 h-px bg-[#4F6D8A]/60 inline-block" /> Services
            </p>
            <h1 className="mt-5 font-serif text-[42px] md:text-[64px] leading-[1.03] text-[#111827]">
              Production Studio
            </h1>
            <p className="mt-5 text-[#374151] leading-relaxed max-w-lg">
              From feature films to weddings — every project is approached like a film, with cinema-grade equipment and a director's eye.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                data-testid="services-hero-book"
                href="/book-shoot"
                className="inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300"
              >
                Book a Shoot <ArrowUpRight size={13} />
              </a>
              <a
                data-testid="services-hero-explore"
                href="#services"
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#EEF2F6] hover:border-[#0F2A43] transition-all duration-300"
              >
                Explore Services <ArrowRight size={13} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Production Studio cards + Equipment We Use + Booking CTA banner */}
      <AeroServices />

      <BtsShowcase />
      <DirectorsDesk />

      {/* Free consultation booking form */}
      <ConsultationSection />

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
